/**
 * @fileoverview What a gardener's public pages (GardenPage, GardenItemPage and their
 * parts in gardenPageParts) work out, apart from drawing: image addresses,
 * short dates, a bed's crop line, a plant's photos to scroll through, and the garden's credit
 * for a sign-up, and how long ago a listed garden was updated. Kept apart from the components so fast refresh keeps
 * working on them.
 *
 * Where: GardenPage, GardenItemPage, gardenPageParts.
 * API: none.
 * Settings: VITE_API_URL; the referral keys in localStorage (shares
 * REFERRAL_KEY, REFERRAL_AT_KEY, REFERRAL_NAME_KEY), plumbing to sign-up.
 * Whether a kept credit still counts is the server's, at sign-up
 * (welcome.REFERRAL_DAYS; audit A73).
 * Words (en/es): gardenPage.page.and, gardensPage.updated.*. A stage's name is the server's, already in the
 * visitor's language (GardenStage name; bloom-backend services/stages/stageKinds, batch 10b A63), printed as sent.
 *
 * @module gardenPage
 */

import React from 'react';
import { ALL_DATE_PARTS, DateTime, formatDateParts, type DateParts } from '@/shared/utils/dateUtils';
import { REFERRAL_AT_KEY, REFERRAL_KEY, REFERRAL_NAME_KEY } from '@/shared/api/shares';
import type { GardenPhoto, GardenPlant } from '@/shared/api/gardens';

/** The translator useTranslation gives. */
export type Translate = (key: string, params?: Record<string, string | number>) => string;

/** Where the API is, as the app is built to reach it. */
const API = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api';

/**
 * The backend's image paths are relative to the API; this makes them
 * absolute for an <img>, as the share page does.
 *
 * @param path - "/api/…", or a full address.
 * @returns An address an <img> can load.
 */
export function apiSrc(path: string): string {
  return path.startsWith('/api/') ? `${API}${path.slice(4)}` : path;
}

/**
 * A day as "Jul 3" (with the year when it is not this year), or null for
 * none or one that cannot be read. With parts, only the parts the gardener
 * shows ("July 2024", "Jul 3"; Profile > Public pages): a page's components
 * take them from its DatePartsContext through useShortDay.
 *
 * @param day - YYYY-MM-DD, or nothing.
 * @param parts - Which parts of a date to say; every part when not given.
 * @returns The short day, or null.
 */
export function shortDay(day: string | null | undefined, parts: DateParts = ALL_DATE_PARTS): string | null {
  if (!day) return null;
  const parsed = DateTime.fromISO(day.slice(0, 10));
  return parsed.isValid ? formatDateParts(parsed, parts, 'short') : null;
}

/**
 * Which parts of a date the page being read shows: the gardener's choice
 * (page.dates from the backend), set by GardenPage, GardenItemPage and
 * SharePage around what they draw. Every part outside a page.
 */
export const DatePartsContext = React.createContext<DateParts>(ALL_DATE_PARTS);

/**
 * shortDay with the page's own parts of a date (DatePartsContext).
 *
 * @returns A function that says a day as this page's gardener shows dates, or null for none.
 */
export function useShortDay(): (day: string | null | undefined) => string | null {
  const parts = React.useContext(DatePartsContext);
  return React.useCallback((day: string | null | undefined) => shortDay(day, parts), [parts]);
}

/**
 * The parts of a date a page's data carries, as the page's context holds them.
 *
 * @param dates - page.dates as the backend sends it.
 * @returns The parts to show.
 */
export function datePartsOf(dates: DateParts): DateParts {
  return { day: dates.day, year: dates.year };
}

/**
 * How long ago a garden was updated, in words: "Updated today", "Updated 2
 * days ago", "Updated last week", and past a month the day itself.
 *
 * @param day - YYYY-MM-DD, or nothing.
 * @param t - The translator.
 * @param now - The moment to count from (the present; for tests).
 * @returns The line, or null with no day (or one that cannot be read).
 */
export function updatedWords(day: string | null | undefined, t: Translate, now: Date = new Date()): string | null {
  if (!day) return null;
  const then = Date.parse(`${day.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(then)) return null;
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const days = Math.max(0, Math.round((today - then) / 86_400_000));
  if (days === 0) return t('gardensPage.updated.today');
  if (days === 1) return t('gardensPage.updated.yesterday');
  if (days < 7) return t('gardensPage.updated.days', { count: days });
  if (days < 14) return t('gardensPage.updated.week');
  if (days < 35) return t('gardensPage.updated.weeks', { count: Math.floor(days / 7) });
  const shown = shortDay(day);
  return shown ? t('gardensPage.updated.on', { day: shown }) : null;
}

/**
 * Keeps this garden as the credit for a sign-up from this browser, with its
 * gardener's name for "Started from Amy's garden", and when it was opened,
 * as a share page keeps its share's: the page opened last holds the credit.
 * Whether it still counts at sign-up is the server's (welcome.REFERRAL_DAYS,
 * from when it was opened; audit A73), so no window is worked out here.
 *
 * @param code - The garden's code, or nothing yet.
 * @param name - The gardener's name as shown, or nothing.
 */
export function useGardenCredit(code: string | null | undefined, name: string | null | undefined): void {
  React.useEffect(() => {
    if (!code) return;
    try {
      window.localStorage.setItem(REFERRAL_KEY, code);
      // When, sent with sign-up so the server can tell an old one.
      window.localStorage.setItem(REFERRAL_AT_KEY, String(Date.now()));
      if (name) window.localStorage.setItem(REFERRAL_NAME_KEY, name);
      else window.localStorage.removeItem(REFERRAL_NAME_KEY);
    } catch { /* private mode */ }
  }, [code, name]);
}

/**
 * "Tomatoes and peppers": a few crops joined in the visitor's language, the
 * first capitalised.
 *
 * @param crops - Plural crop names.
 * @param t - The translator.
 * @returns The line, or null with no crops.
 */
export function cropLine(crops: string[] | undefined, t: Translate): string | null {
  const list = (crops ?? []).map((crop) => crop.toLowerCase()).filter(Boolean);
  if (!list.length) return null;
  const joined = list.length === 1 ? list[0] : t('gardenPage.page.and', { first: list.slice(0, -1).join(', '), second: list[list.length - 1] });
  return joined.charAt(0).toUpperCase() + joined.slice(1);
}

/**
 * What a plant card scrolls through: its shared photos, its seed-packet
 * picture last (the server's photos).
 *
 * @param plant - The plant.
 * @returns The photos, newest first; none for a plant with no public photo.
 */
export function photosOfPlant(plant: GardenPlant): GardenPhoto[] {
  return plant.photos;
}
